import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Pagination,
  Paper,
  Chip,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  OutlinedInput,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";

import {
  expertAllCategoryApi,
  expertAllSubCategoryApi,
  ExpertSubCategoryApi,
  freelancerApi,
  serviceCategoryApi,
} from "../Component/API/Api";
import ExpertListBanner from "../Component/ExpertList Component/ExpertListBanner";
import GetStarted from "../Component/Get Started/GetStarted";
import FreelancerItem from "../Component/Homepage Components/Freelancer/FreelancerItem";
import { Close, Search } from "@mui/icons-material";

const PAGE_SIZE_FALLBACK = 12;

const ACCENT = "#0F766E"; // teal accent used across the filter bar
const ACCENT_SOFT = "rgba(15, 118, 110, 0.08)";
const ACCENT_BORDER = "rgba(15, 118, 110, 0.35)";

// Shared "pill" look for the two Select filters — a soft tint + accent
// border once a value is chosen, so the filter bar visually communicates
// active state at a glance.
const pillSelectSx = (hasValue, disabled) => ({
  borderRadius: 999,
  bgcolor: hasValue ? ACCENT_SOFT : "background.paper",
  opacity: disabled ? 0.55 : 1,
  transition: "border-color 0.15s ease, background-color 0.15s ease",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: hasValue ? ACCENT_BORDER : "divider",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: disabled ? undefined : ACCENT,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
    borderWidth: 1.5,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    gap: 1,
    py: 1.15,
    pl: 2,
    pr: 4,
    fontSize: 14.5,
    fontWeight: hasValue ? 600 : 500,
    color: hasValue ? ACCENT : "text.primary",
    whiteSpace: "nowrap",
  },
  "& .MuiSelect-icon": {
    color: hasValue ? ACCENT : "text.secondary",
  },
});

// Rounded, elevated dropdown panel (instead of the default square Paper) —
// shared by both Select filters.
const selectMenuProps = {
  PaperProps: {
    elevation: 0,
    sx: {
      mt: 1,
      borderRadius: 2.5,
      border: "1px solid",
      borderColor: "divider",
      boxShadow: "0 16px 40px -12px rgba(15, 23, 42, 0.22)",
      maxHeight: 340,
      minWidth: 200,
      py: 0.5,
      "& .MuiMenuItem-root": {
        borderRadius: 1.5,
        mx: 0.75,
        my: 0.25,
        px: 1.5,
        fontSize: 14,
        "&:hover": { bgcolor: ACCENT_SOFT },
        "&.Mui-selected": {
          bgcolor: ACCENT_SOFT,
          fontWeight: 600,
          "&:hover": { bgcolor: ACCENT_SOFT },
        },
      },
    },
  },
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

const ExpertList = () => {
  const navigate = useNavigate();

  const [freelancerData, setFreelancerData] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // --- Debounce the search box so we don't fire a request on every keystroke ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // --- Fetch Categories ---
  useEffect(() => {
    fetch(serviceCategoryApi)
      .then((res) => res.json())
      .then((data) => {
        setCategoryList(Array.isArray(data) ? data : []);
      })
      .catch((err) => console.error("Category API Error:", err));
  }, []);

  // --- Fetch Subcategories when Category changes ---
  useEffect(() => {
    if (!selectedCategory) {
      setSubCategoryList([]);
      setSelectedSubCategory("");
      return;
    }

    const foundCategory = categoryList.find(
      (c) => c.category_name === selectedCategory
    );
    if (!foundCategory) return;

    const formData = new FormData();
    formData.append("pref", foundCategory.category_id);
    formData.append("action", "fetch");

    fetch(ExpertSubCategoryApi, {
      method: "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        setSubCategoryList(Array.isArray(data) ? data : []);
        setSelectedSubCategory("");
      })
      .catch((err) => console.error("SubCategory API Error:", err));
  }, [selectedCategory, categoryList]);

  // --- Fetch Freelancers ---
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const formData = new FormData();
      let endpoint = freelancerApi;

      // Priority: SubCategory > Category > Default
      if (selectedSubCategory) {
        const foundSub = subCategoryList.find(
          (s) => s.sub_name === selectedSubCategory
        );
        if (!foundSub) return;

        formData.append("pref", foundSub.sub_id);
        endpoint = expertAllSubCategoryApi;
      } else if (selectedCategory) {
        const foundCategory = categoryList.find(
          (c) => c.category_name === selectedCategory
        );
        if (!foundCategory) return;

        formData.append("pref", foundCategory.category_id);
        endpoint = expertAllCategoryApi;
      }

      formData.append("action", "fetch");
      formData.append("page", page);
      // NOTE: adjust this key ("search") if your backend expects a different
      // field name (e.g. "keyword" or "query") for a text search.
      if (debouncedSearchTerm) {
        formData.append("search", debouncedSearchTerm);
      }

      setLoading(true);
      setError("");

      try {
        const res = await fetch(endpoint, { method: "POST", body: formData });
        const result = await res.json();
        if (cancelled) return;

        const data = result?.data ?? result;
        setFreelancerData(Array.isArray(data) ? data : []);

        if (result?.total_pages) {
          setTotalPages(result.total_pages);
        } else if (result?.total && result?.per_page) {
          setTotalPages(Math.ceil(result.total / result.per_page));
        } else {
          setTotalPages(1);
        }
        setTotalResults(
          typeof result?.total === "number" ? result.total : null
        );
      } catch (err) {
        if (!cancelled) {
          console.error("Freelancer Fetch Error:", err);
          setError("We couldn't load experts right now. Please try again.");
          setFreelancerData([]);
          setTotalPages(1);
          setTotalResults(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selectedCategory, selectedSubCategory, page, categoryList, subCategoryList, debouncedSearchTerm]);

  // --- Handlers ---
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1);
  };

  const handleSubCategoryChange = (event) => {
    setSelectedSubCategory(event.target.value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setSelectedSubCategory("");
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(1);
  };

  const hasActiveFilters = Boolean(
    selectedCategory || selectedSubCategory || debouncedSearchTerm
  );

  return (
    <Box sx={{ marginTop: { xs: "60px", md: "0px" }, bgcolor: "#F7F8FA", minHeight: "100vh" }}>
      {/* <ExpertListBanner /> */}

      <Container sx={{ mt: 0, pb: 6, px: { xs: 2, sm: 3 } }}>
        {/* Filter bar */}
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 3, md: 4 },
            p: { xs: 2, sm: 2.5, md: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            boxShadow:
              "0 1px 2px rgba(16,24,40,0.04), 0 12px 28px -16px rgba(16,24,40,0.10)",
          }}
        >
          {/* <TextField
            fullWidth
            size="small"
            placeholder="Search experts by name or skill"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} edge="end" aria-label="Clear search">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: 999,
                bgcolor: "background.paper",
                fontSize: 15,
                pl: 0.5,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: ACCENT,
                  borderWidth: 1.5,
                },
              },
            }}
          /> */}

          <OutlinedInput
            fullWidth
            size="small"
            placeholder="Search agencies by name or skill…"
            value={searchTerm}
            onChange={handleSearchChange}
            // onKeyDown={handleSearchKeyDown}
            startAdornment={
              <InputAdornment position="start">
                <Search sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                {searchTerm && (
                  <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                    <Close fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  // onClick={handleSearchNow}
                  aria-label="Search"
                  sx={{
                    ml: 0.5,
                    bgcolor: ACCENT,
                    color: "#fff",
                    "&:hover": { bgcolor: "#0C5D57" },
                  }}
                >
                  <Search fontSize="small" />
                </IconButton>
              </InputAdornment>
            }
            sx={{
              borderRadius: 999,
              bgcolor: "background.paper",
              fontSize: 15,
              pl: 1,
              pr: 0.75,
              py: 0.25,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: ACCENT,
                borderWidth: 1.5,
              },
            }}
          />

          {/* Category / subcategory — compact pill selects, wrap freely on small screens */}
          <Stack
            direction="row"
            flexWrap="wrap"
            spacing={1.25}
            sx={{ mt: 2, rowGap: 1.25 }}
          >
            <FormControl
              size="small"
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 190 } }}
            >
              <Select value={selectedCategory} onChange={handleCategoryChange} displayEmpty
                MenuProps={selectMenuProps}
                sx={pillSelectSx(Boolean(selectedCategory))}
                renderValue={(val) => (
                  <>
                    <CategoryRoundedIcon
                      sx={{ fontSize: 18, opacity: val ? 1 : 0.55 }}
                    />
                    {val || "All categories"}
                  </>
                )}
              >
                <MenuItem value="">
                  <em>All categories</em>
                </MenuItem>
                {categoryList.map((cat) => (
                  <MenuItem key={cat.category_id} value={cat.category_name}>
                    {cat.category_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              disabled={!selectedCategory}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 190 } }}
            >
              <Select
                value={selectedSubCategory}
                onChange={handleSubCategoryChange}
                displayEmpty
                disabled={!selectedCategory}
                MenuProps={selectMenuProps}
                sx={pillSelectSx(Boolean(selectedSubCategory), !selectedCategory)}
                renderValue={(val) => (
                  <>
                    <LayersRoundedIcon
                      sx={{ fontSize: 18, opacity: val ? 1 : 0.55 }}
                    />
                    {val || "All sub categories"}
                  </>
                )}
              >
                <MenuItem value="">
                  <em>All sub categories</em>
                </MenuItem>
                {subCategoryList.map((sub) => (
                  <MenuItem key={sub.sub_id} value={sub.sub_name}>
                    {sub.sub_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Chip
                label="Clear all"
                size="small"
                variant="outlined"
                onClick={clearFilters}
                sx={{
                  borderRadius: 999,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  fontWeight: 500,
                }}
              />
            )}
          </Stack>

          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              {debouncedSearchTerm && (
                <Chip
                  label={`"${debouncedSearchTerm}"`}
                  size="small"
                  onDelete={handleClearSearch}
                  deleteIcon={<CloseIcon fontSize="small" />}
                  sx={{ borderRadius: 999, bgcolor: ACCENT_SOFT, color: ACCENT, fontWeight: 500 }}
                />
              )}
              {selectedCategory && (
                <Chip
                  label={selectedCategory}
                  size="small"
                  onDelete={() => {
                    setSelectedCategory("");
                    setPage(1);
                  }}
                  deleteIcon={<CloseIcon fontSize="small" />}
                  sx={{ borderRadius: 999, bgcolor: ACCENT_SOFT, color: ACCENT, fontWeight: 500 }}
                />
              )}
              {selectedSubCategory && (
                <Chip
                  label={selectedSubCategory}
                  size="small"
                  onDelete={() => {
                    setSelectedSubCategory("");
                    setPage(1);
                  }}
                  deleteIcon={<CloseIcon fontSize="small" />}
                  sx={{ borderRadius: 999, bgcolor: ACCENT_SOFT, color: ACCENT, fontWeight: 500 }}
                />
              )}
            </Stack>
          )}
        </Paper>

        {/* Results summary */}
        {!error && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={0.5}
            sx={{ mt: 3, mb: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              {loading
                ? "Loading experts…"
                : totalResults !== null
                ? `${totalResults} expert${totalResults === 1 ? "" : "s"} found`
                : `${freelancerData.length} expert${freelancerData.length === 1 ? "" : "s"} on this page`}
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Freelancer Grid — 1 col on phones, 2 on small tablets, 3 on desktop, 4 on wide screens */}
        <Grid container spacing={2} mb={4}>
          {loading ? (
            Array.from({ length: PAGE_SIZE_FALLBACK }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${i}`}>
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
                <Skeleton variant="text" sx={{ mt: 1 }} />
                <Skeleton variant="text" width="60%" />
              </Grid>
            ))
          ) : freelancerData.length > 0 ? (
            freelancerData.map((freelancer) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={freelancer.refer_id}>
                <FreelancerItem freelancerData={freelancer} />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  py: 6,
                  px: 2,
                  textAlign: "center",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 3,
                }}
              >
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  No experts found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try a different search term, category, or clear your filters.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Stack alignItems="center" sx={{ mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              color="primary"
              siblingCount={0}
              boundaryCount={1}
              showFirstButton
              showLastButton
              size="medium"
              sx={{
                mb: 4,
                "& .MuiPaginationItem-root": { borderRadius: 999 },
              }}
            />
          </Stack>
        )}
      </Container>

      <GetStarted />
    </Box>
  );
};

export default ExpertList;